import { useEffect, useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import { sorterToOrdering } from 'services/utils';
import type { Study, StudyListParams } from '@/models/study';

export type StatusSegment = 'all' | 'unread' | 'finalized';

const STATUS_MAP: Record<StatusSegment, string[] | undefined> = {
  all: undefined,
  unread: ['DRAFT', 'PRELIMINARY'],
  finalized: ['FINAL'],
};

export function useAdminStudyFilters() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusSegment, setStatusSegment] = useState<StatusSegment>('all');
  const [isAssigned, setIsAssigned] = useState<boolean | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedReferringPhysicians, setSelectedReferringPhysicians] =
    useState<string[]>([]);
  const [selectedInterpretingPhysicians, setSelectedInterpretingPhysicians] =
    useState<string[]>([]);
  const [selectedSonographers, setSelectedSonographers] = useState<string[]>(
    []
  );
  const [selectedModalities, setSelectedModalities] = useState<string[]>([]);

  // ---- debounced search (300ms per spec)
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchText]);

  // ---- reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    statusSegment,
    isAssigned,
    dateRange,
    selectedPriorities,
    selectedSites,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
    selectedModalities,
  ]);

  // ---- derived flags
  const hasActiveFilters = useMemo(
    () =>
      debouncedSearch.length > 0 ||
      statusSegment !== 'all' ||
      isAssigned !== undefined ||
      dateRange !== null ||
      selectedPriorities.length > 0 ||
      selectedSites.length > 0 ||
      selectedReferringPhysicians.length > 0 ||
      selectedInterpretingPhysicians.length > 0 ||
      selectedSonographers.length > 0 ||
      selectedModalities.length > 0,
    [
      debouncedSearch,
      statusSegment,
      isAssigned,
      dateRange,
      selectedPriorities,
      selectedSites,
      selectedReferringPhysicians,
      selectedInterpretingPhysicians,
      selectedSonographers,
      selectedModalities,
    ]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearch.length > 0) count += 1;
    if (statusSegment !== 'all') count += 1;
    if (isAssigned !== undefined) count += 1;
    if (dateRange !== null) count += 1;
    if (selectedPriorities.length > 0) count += 1;
    if (selectedSites.length > 0) count += 1;
    if (selectedReferringPhysicians.length > 0) count += 1;
    if (selectedInterpretingPhysicians.length > 0) count += 1;
    if (selectedSonographers.length > 0) count += 1;
    if (selectedModalities.length > 0) count += 1;
    return count;
  }, [
    debouncedSearch,
    statusSegment,
    isAssigned,
    dateRange,
    selectedPriorities,
    selectedSites,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
    selectedModalities,
  ]);

  // ---- assembled params
  const params = useMemo<StudyListParams>(() => {
    const reportStatus = STATUS_MAP[statusSegment];
    return {
      page,
      size,
      ordering,
      search: debouncedSearch || undefined,
      report_status: reportStatus,
      is_assigned: isAssigned,
      study_date_from: dateRange?.[0],
      study_date_to: dateRange?.[1],
      priority: selectedPriorities.length > 0 ? selectedPriorities : undefined,
      site: selectedSites.length > 0 ? selectedSites : undefined,
      referring_physician:
        selectedReferringPhysicians.length > 0
          ? selectedReferringPhysicians
          : undefined,
      interpreting_physician:
        selectedInterpretingPhysicians.length > 0
          ? selectedInterpretingPhysicians
          : undefined,
      sonographer:
        selectedSonographers.length > 0 ? selectedSonographers : undefined,
      modality: selectedModalities.length > 0 ? selectedModalities : undefined,
    };
  }, [
    page,
    size,
    ordering,
    debouncedSearch,
    statusSegment,
    isAssigned,
    dateRange,
    selectedPriorities,
    selectedSites,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
    selectedModalities,
  ]);

  // ---- callbacks
  const onTableChange: TableProps<Study>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    setPage(pagination?.current ?? page);
    setSize(pagination?.pageSize ?? size);
    setOrdering(sorterToOrdering(sorter));
  };

  const clearFilters = () => {
    setSearchText('');
    setDebouncedSearch('');
    setStatusSegment('all');
    setIsAssigned(undefined);
    setDateRange(null);
    setSelectedPriorities([]);
    setSelectedSites([]);
    setSelectedReferringPhysicians([]);
    setSelectedInterpretingPhysicians([]);
    setSelectedSonographers([]);
    setSelectedModalities([]);
  };

  return {
    params,
    onTableChange,
    hasActiveFilters,
    activeFilterCount,
    clearFilters,

    searchText,
    onSearchChange: setSearchText,
    statusSegment,
    onStatusSegmentChange: setStatusSegment,
    isAssigned,
    onAssignedChange: setIsAssigned,
    dateRange,
    onDateRangeChange: setDateRange,
    selectedPriorities,
    onPrioritiesChange: setSelectedPriorities,
    selectedSites,
    onSitesChange: setSelectedSites,
    selectedReferringPhysicians,
    onReferringPhysiciansChange: setSelectedReferringPhysicians,
    selectedInterpretingPhysicians,
    onInterpretingPhysiciansChange: setSelectedInterpretingPhysicians,
    selectedSonographers,
    onSonographersChange: setSelectedSonographers,
    selectedModalities,
    onModalitiesChange: setSelectedModalities,
  };
}
